import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { isTokenExpired, willTokenExpireSoon, getTokenExpirationTime, formatTimeRemaining } from '@/utils/jwtUtils';
import { showErrorToast, showWarningToast, showInfoToast } from '@/lib/toast-utils';
import { clearGroupsCache } from '@/hooks/useGroupChildren';
import { clearUserInfoCache } from '@/hooks/useUserInfo';
import { clearUnitsCache } from '@/lib/unitsCache';
import { clearRolesCache } from '@/lib/rolesCache';
import { clearUnitDetailCache } from '@/lib/unitDetailCache';
import { clearDigitalAssetsCache } from '@/lib/digitalAssetsCache';
import { clearMessageBoardCache } from '@/lib/messageBoardCache';
import { clearRoleDefinitionsCache } from '@/lib/roleDefinitionsCache';

interface User {
  id: string;
  username: string;
  token: string;
}

interface AuthContextType {
  user: User | null;
  login: (username: string, password: string) => Promise<{ error?: string }>;
  logout: () => void;
  isLoading: boolean;
  recoverPassword: (email: string) => Promise<{ error?: string }>;
  resetPassword: (token: string, newPassword: string) => Promise<{ error?: string }>;
  refreshToken: () => Promise<{ error?: string }>;
  getValidToken: () => Promise<string | null>;
  isSessionExpiringSoon: boolean;
  timeUntilExpiration: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSessionExpiringSoon, setIsSessionExpiringSoon] = useState(false);
  const [timeUntilExpiration, setTimeUntilExpiration] = useState<string | null>(null);
  
  
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const warningShownRef = useRef(false);
  const lastActivityRef = useRef(Date.now());

  const handleSessionExpired = useCallback(() => {
    setUser(null);
    localStorage.removeItem('user');
    setIsSessionExpiringSoon(false);
    setTimeUntilExpiration(null);
    warningShownRef.current = false;
    
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    
    showErrorToast("Your session has expired. Please log in again.", "Session Expired");
  }, []);

  const refreshToken = useCallback(async (): Promise<{ error?: string }> => {
    try {
      if (!user?.token) {
        return { error: 'No token to refresh' };
      }

      const { data, error } = await supabase.functions.invoke('refresh-token', {
        body: { token: user.token }
      });

      if (error) {
        console.error('Token refresh error:', error);
        return { error: 'Token refresh failed' };
      }

      if (data?.error) {
        console.error('Token refresh API error:', data.error);
        return { error: data.error };
      }

      if (!data?.token) {
        return { error: 'Invalid refresh response' };
      }

      const updatedUser = { ...user, token: data.token };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      setIsSessionExpiringSoon(false);
      warningShownRef.current = false;

      console.log('Token refreshed successfully');
      return {};
    } catch (error) {
      console.error('Token refresh failed:', error);
      return { error: 'Token refresh failed' };
    }
  }, [user]);

  const getValidToken = useCallback(async (): Promise<string | null> => {
    if (!user?.token) {
      return null;
    }

    if (isTokenExpired(user.token)) {
      console.log('Token expired, attempting refresh...');
      const refreshResult = await refreshToken();
      if (refreshResult.error) {
        console.error('Failed to refresh expired token:', refreshResult.error);
        handleSessionExpired();
        return null;
      }
      const savedUser = localStorage.getItem('user');
      if (savedUser) {
        const parsedUser = JSON.parse(savedUser);
        return parsedUser.token;
      }
    }

    return user.token;
  }, [user, refreshToken, handleSessionExpired]);

  const validateToken = useCallback(() => {
    if (!user?.token) {
      return;
    }

    if (isTokenExpired(user.token)) {
      console.log('Token has expired during validation');
      handleSessionExpired();
      return;
    }

    const timeLeft = getTokenExpirationTime(user.token);
    if (timeLeft !== null) {
      setTimeUntilExpiration(formatTimeRemaining(timeLeft));
      
      const expiringSoon = willTokenExpireSoon(user.token, 600);
      setIsSessionExpiringSoon(expiringSoon);
      
      if (expiringSoon && !warningShownRef.current) {
        warningShownRef.current = true;
        showWarningToast(`Your session will expire in ${formatTimeRemaining(timeLeft)}. It will be automatically refreshed.`, "Session Expiring Soon");
      }
      
      const now = Date.now();
      const recentActivity = now - lastActivityRef.current < 30 * 60 * 1000;
      if (willTokenExpireSoon(user.token, 300) && recentActivity) {
        console.log('Auto-refreshing token due to upcoming expiration');
        refreshToken().catch(error => {
          console.error('Auto-refresh failed:', error);
        });
      }
    }
  }, [user?.token, handleSessionExpired, refreshToken]);

  useEffect(() => {
    const handleActivity = () => {
      lastActivityRef.current = Date.now();
    };

    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
    };
  }, []);

  useEffect(() => {
    if (user?.token) {
      validateToken();
      
      intervalRef.current = setInterval(validateToken, 60000);
      
      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      };
    }
  }, [user?.token, validateToken]);

  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);
        
        if (isTokenExpired(parsedUser.token)) {
          console.log('Saved token is expired, clearing session');
          localStorage.removeItem('user');
        } else {
          setUser(parsedUser);
        }
      } catch (error) {
        console.error('Error parsing saved user:', error);
        localStorage.removeItem('user');
      }
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const response = await originalFetch(...args);
      
      if ((response.status === 401 || response.status === 403) && user) {
        const responseText = await response.clone().text();
        if (responseText.includes('authentication') || responseText.includes('token') || responseText.includes('unauthorized')) {
          handleSessionExpired();
        }
      }
      
      return response;
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, [user, handleSessionExpired]);

  const login = async (username: string, password: string): Promise<{ error?: string }> => {
    try {
      const { data: authData, error: authError } = await supabase.functions.invoke('authenticate', {
        body: { 
          username,
          password,
          shopId: "",
          expirationInSeconds: "86400"
        }
      });

      if (authError) {
        return { error: 'Your username or password is invalid. Please try again' };
      }

      if (authData && authData.error) {
        return { error: 'Your username or password is invalid. Please try again' };
      }

      if (!authData?.token) {
        return { error: 'Invalid credentials' };
      }

      const userData: User = {
        id: username,
        username,
        token: authData.token
      };

      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      sessionStorage.removeItem('hidePrices');
      
      return {};
    } catch (error) {
      return { error: 'Login failed. Please try again.' };
    }
  };

  const logout = useCallback(() => {
    setUser(null);
    localStorage.removeItem('user');
    sessionStorage.removeItem('hidePrices');
    sessionStorage.removeItem('selectedProfile');
    sessionStorage.removeItem('userProfiles');
    setIsSessionExpiringSoon(false);
    setTimeUntilExpiration(null);
    warningShownRef.current = false;
    clearGroupsCache();
    clearUserInfoCache();
    clearUnitsCache();
    clearRolesCache();
    clearUnitDetailCache();
    clearDigitalAssetsCache();
    clearMessageBoardCache();
    clearRoleDefinitionsCache();
    // Note: FavoritesContext listens for profile changes and resets automatically

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const recoverPassword = async (email: string): Promise<{ error?: string }> => {
    try {
      const { data, error } = await supabase.functions.invoke('recover-password', {
        body: { email }
      });

      if (error) {
        return { error: `Password recovery failed: ${error.message}` };
      }

      if (data && data.error) {
        return { error: `Password recovery failed: ${data.error}` };
      }

      return {};
    } catch (error) {
      return { error: 'Password recovery failed. Please try again.' };
    }
  };

  const resetPassword = async (token: string, newPassword: string): Promise<{ error?: string }> => {
    try {
      const { data, error } = await supabase.functions.invoke('reset-password', {
        body: { token, newPassword }
      });

      if (error) {
        return { error: `Password reset failed: ${error.message}` };
      }

      if (data && data.error) {
        return { error: `Password reset failed: ${data.error}` };
      }

      return {};
    } catch (error) {
      return { error: 'Password reset failed. Please try again.' };
    }
  };

  const value = {
    user,
    login,
    logout,
    isLoading,
    recoverPassword,
    resetPassword,
    refreshToken,
    getValidToken,
    isSessionExpiringSoon,
    timeUntilExpiration,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
